// Client-side DOCX export for Get Who To Buy Strategy Tool Result
import { HeadingLevel, Packer, Paragraph, TextRun, Document } from 'docx';

// Helper to create heading
const createHeading = (text, level = HeadingLevel.HEADING_2) => [
  new Paragraph({ text: '', spacing: { after: 400 } }),
  new Paragraph({
    children: [
      new TextRun({
        text: text || '',
        bold: true,
        color: level === HeadingLevel.HEADING_1 ? '1565C0' : '1976D2',
        size: level === HeadingLevel.HEADING_1 ? 32 : 28
      })
    ],
    heading: level
  }),
  new Paragraph({ text: '', spacing: { after: 200 } })
];

// Helper to create bullet list
const createBulletList = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  return items
    .filter((item) => item && typeof item === 'string' && item.trim() !== '')
    .map(
      (item) =>
        new Paragraph({
          text: item.trim(),
          bullet: { level: 0 },
          spacing: { before: 120, after: 120 }
        })
    );
};

// Helper to parse markdown and inline formatting (bold, italic, links)
const splitInline = (text) => {
  if (!text) return [{ text: '' }];
  const parts = [];
  let i = 0;

  while (i < text.length) {
    // Markdown link [text](url)
    if (text[i] === '[') {
      const closeBracket = text.indexOf(']', i + 1);
      if (closeBracket !== -1 && text[closeBracket + 1] === '(') {
        const closeParen = text.indexOf(')', closeBracket + 2);
        if (closeParen !== -1) {
          const label = text.substring(i + 1, closeBracket);
          const url = text.substring(closeBracket + 2, closeParen);
          parts.push({ text: label, link: url });
          i = closeParen + 1;
          continue;
        }
      }
    }
    // Bold **text**
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        const boldText = text.substring(i + 2, end);
        parts.push({ text: boldText, bold: true });
        i = end + 2;
        continue;
      }
    }
    // Italic *text*
    if (text[i] === '*' && text[i + 1] !== '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        const italicText = text.substring(i + 1, end);
        parts.push({ text: italicText, italics: true });
        i = end + 1;
        continue;
      }
    }
    // Plain char run until next special token
    let j = i + 1;
    while (j < text.length && !'[*'.includes(text[j])) j++;
    parts.push({ text: text.substring(i, j) });
    i = j;
  }
  return parts;
};

// Helper to create paragraph with inline formatting (including links)
const createFormattedParagraph = (text) => {
  const runs = splitInline(text).map((part) => {
    if (part.link) {
      // For links, show text with underline and color (URL shown in parentheses)
      return new TextRun({
        text: `${part.text} (${part.link})`,
        color: '1976D2',
        underline: {}
      });
    }
    return new TextRun({
      text: part.text,
      bold: !!part.bold,
      italics: !!part.italics
    });
  });

  return new Paragraph({
    children: runs.length > 0 ? runs : [new TextRun({ text })]
  });
};

// Helper to create blockquote (for notes)
const createBlockquote = (text) => {
  return new Paragraph({
    children: [new TextRun({ text, italics: true })],
    indent: { left: 480 },
    spacing: { before: 200, after: 200 }
  });
};

export async function exportGetWhoToBuyDocx(strategyData, fileName = 'get-who-to-buy-strategy.docx') {
  const children = [];

  // Title
  children.push(...createHeading('Strategic Framework', HeadingLevel.HEADING_1));

  if (strategyData.client) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: `for ${strategyData.client}`, size: 24 })],
        spacing: { after: 600 }
      })
    );
  }

  if (strategyData.category) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: strategyData.category, italics: true, color: '757575' })],
        spacing: { after: 600 }
      })
    );
  }

  // GET/WHO/TO/BY Framework
  children.push(...createHeading('Strategic Framework', HeadingLevel.HEADING_2));

  const frameworkSections = [
    { key: 'get', label: 'Get', content: strategyData.get },
    { key: 'who', label: 'Who', content: strategyData.who },
    { key: 'to', label: 'To', content: strategyData.to },
    { key: 'by', label: 'By', content: strategyData.by }
  ];

  frameworkSections.forEach((section) => {
    if (section.content) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: section.label, bold: true, size: 28, color: '1565C0' })],
          spacing: { before: 400, after: 200 }
        })
      );
      children.push(createFormattedParagraph(section.content));
    }
  });

  // Strategic Insights
  children.push(...createHeading('Strategic Insights', HeadingLevel.HEADING_2));

  if (strategyData.key_strategic_insight) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Key Strategic Insight', bold: true, size: 24 })],
        spacing: { before: 300, after: 200 }
      })
    );
    children.push(createFormattedParagraph(strategyData.key_strategic_insight));
  }

  if (strategyData.creative_implication) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Creative Implication', bold: true, size: 24 })],
        spacing: { before: 300, after: 200 }
      })
    );
    children.push(createFormattedParagraph(strategyData.creative_implication));
  }

  // Supporting Data Points
  if (strategyData.supporting_data_points && strategyData.supporting_data_points.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Supporting Data Points', bold: true, size: 24 })],
        spacing: { before: 300, after: 200 }
      })
    );
    children.push(...createBulletList(strategyData.supporting_data_points));
  }

  // Tone & Channel Direction
  if (strategyData.tone_channel_direction) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Tone & Channel Direction', bold: true, size: 24 })],
        spacing: { before: 300, after: 200 }
      })
    );

    Object.entries(strategyData.tone_channel_direction).forEach(([key, value]) => {
      if (value) {
        const label = key
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        children.push(
          new Paragraph({
            children: [new TextRun({ text: `${label}: `, bold: true }), new TextRun({ text: value })],
            spacing: { before: 200, after: 200 }
          })
        );
      }
    });
  }

  // Strategic Guardrails
  if (strategyData.strategic_guardrails) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Strategic Guardrails', bold: true, size: 24 })],
        spacing: { before: 300, after: 200 }
      })
    );

    Object.entries(strategyData.strategic_guardrails).forEach(([key, value]) => {
      if (value) {
        const label = key
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        children.push(
          new Paragraph({
            children: [new TextRun({ text: `${label}: `, bold: true }), new TextRun({ text: value })],
            spacing: { before: 200, after: 200 }
          })
        );
      }
    });
  }

  // Supporting Text
  if (strategyData.supporting_text) {
    children.push(...createHeading('Additional Context', HeadingLevel.HEADING_2));
    const paragraphs = strategyData.supporting_text.split(/\n\n+/);
    paragraphs.forEach((para) => {
      const trimmed = para.trim();
      if (trimmed) {
        children.push(createFormattedParagraph(trimmed));
      }
    });
  }

  // Notes
  if (strategyData.notes_data_recency) {
    children.push(new Paragraph({ text: '', spacing: { before: 400 } }));
    children.push(createBlockquote(`Note: ${strategyData.notes_data_recency}`));
  }

  // Create document
  const doc = new Document({
    sections: [{ children }]
  });

  // Download
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default exportGetWhoToBuyDocx;
